import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { apiGet, apiPut, apiPost, fmtDate } from "@/lib/shieldx";
import { PageHeader, Loader } from "@/components/shieldx/Primitives";
import { Table } from "@/components/shieldx/Table";
import { StatusBadge, RiskBadge } from "@/components/shieldx/Badges";
import { SecBadge, PencilBtn, ActionMenu, ActionDialog, EditDialog, Drawer, Timeline, KV, FilterBar } from "@/components/shieldx/SuperAdmin";
import { Boxes, Eye, BadgeCheck, Ban, RotateCcw, ShieldX } from "lucide-react";

export default function SAAssets() {
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [edit, setEdit] = useState(null);
  const [action, setAction] = useState(null);

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (search) p.set("q", search);
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    setRows(await apiGet(`/super-admin/assets?${p.toString()}`));
  }, [search, filters]);
  useEffect(() => { const t = setTimeout(load, 220); return () => clearTimeout(t); }, [load]);

  const open360 = async (a) => { setSel(a); setDetail(null); setDetailLoading(true); try { setDetail(await apiGet(`/super-admin/assets/${a.id}/360`)); } finally { setDetailLoading(false); } };
  const runAction = async ({ reason, step_up_token, ...extra }) => {
    const { a, act } = action;
    await apiPost(`/super-admin/assets/${a.id}/action`, { action: act, reason, step_up_token, ...extra });
    toast.success(`${act} applied to ${a.name}`); load();
  };
  const saveEdit = async (vals) => { await apiPut(`/super-admin/assets/${edit.id}`, { ...vals, reason: "Super Admin field edit" }); toast.success("Asset updated"); load(); };
  if (!rows) return <Loader label="Loading global asset inventory" />;

  const columns = [
    { key: "name", label: "Asset", render: (r) => <div><p className="font-medium text-slate-100">{r.name}</p><p className="font-mono text-[11px] text-slate-500">{r.org_name}</p></div> },
    { key: "type", label: "Type" }, { key: "environment", label: "Env" },
    { key: "risk_level", label: "Risk", render: (r) => <RiskBadge value={r.risk_level} /> },
    { key: "security_status", label: "Security", render: (r) => <SecBadge value={r.security_status} /> },
    { key: "incident_count", label: "Incidents", render: (r) => <span className="font-mono text-slate-300">{r.incident_count}</span> },
    { key: "actions", label: "", render: (r) => (
      <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => open360(r)} data-testid={`asset-view360-${r.id}`} className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-700/60 bg-slate-800/50 px-2.5 text-xs text-slate-300 hover:border-blue-500/50 hover:text-blue-300"><Eye className="h-3.5 w-3.5" /> 360°</button>
        <PencilBtn onClick={() => setEdit(r)} testid={`asset-edit-${r.id}`} />
        <ActionMenu testid={`asset-menu-${r.id}`} items={[
          { label: "Verify", icon: BadgeCheck, onClick: () => setAction({ a: r, act: "VERIFY" }) },
          { label: "Re-verify", icon: RotateCcw, onClick: () => setAction({ a: r, act: "REVERIFY" }) },
          { label: "Reactivate", icon: RotateCcw, onClick: () => setAction({ a: r, act: "REACTIVATE" }) },
          { label: "Suspend", icon: Ban, danger: true, onClick: () => setAction({ a: r, act: "SUSPEND" }) },
          { label: "Revoke", icon: ShieldX, danger: true, onClick: () => setAction({ a: r, act: "REVOKE" }) },
        ]} />
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader icon={Boxes} title="Global Asset Inventory" subtitle="Every protected asset across all tenants. Open any asset for its full 360° view." />
      <FilterBar searchValue={search} onSearch={setSearch} values={filters} onChange={(k, v) => setFilters((f) => ({ ...f, [k]: v }))}
        searchPlaceholder="Search asset, type, URL…"
        filters={[
          { key: "environment", label: "Environment", options: ["Production", "Staging", "Development"] },
          { key: "risk", label: "Risk", options: ["LOW", "MEDIUM", "HIGH", "CRITICAL"] },
          { key: "protection", label: "Protection", options: ["PROTECTED", "MONITORING", "AT_RISK", "ISOLATED", "DISABLED"] },
        ]} />
      <Table columns={columns} rows={rows} testid="sa-assets-table" onRowClick={open360} empty="No assets match these filters" />

      <EditDialog open={!!edit} onClose={() => setEdit(null)} title={`Edit ${edit?.name || "asset"}`}
        fields={[{ key: "name", label: "Name" }, { key: "type", label: "Type" }, { key: "environment", label: "Environment" }, { key: "risk_level", label: "Risk Level", type: "select", options: ["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((o) => ({ value: o, label: o })) }, { key: "owner", label: "Owner" }, { key: "url", label: "URL" }]}
        initial={edit || {}} onSave={saveEdit} />

      <ActionDialog open={!!action} onClose={() => setAction(null)} action={action?.act} targetType="ASSET" targetId={action?.a?.id}
        title={`${action?.act} — ${action?.a?.name || ""}`} body="This governance action will be recorded in the audit chain." onDone={runAction} />

      <Drawer open={!!sel} onClose={() => { setSel(null); setDetail(null); }} loading={detailLoading}
        title={sel?.name} subtitle={detail?.identity?.org_name} testid="asset-360-drawer"
        badge={detail && <SecBadge value={detail.security_status} />}
        tabs={detail ? [
          { key: "identity", label: "Identity", content: (
            <div className="grid gap-x-8 sm:grid-cols-2">
              <KV label="Asset ID" value={detail.identity.id} mono /><KV label="Organization" value={detail.identity.org_name} />
              <KV label="Type" value={detail.identity.type} /><KV label="Environment" value={detail.identity.environment} />
              <KV label="Owner" value={detail.identity.owner} mono /><KV label="Risk" value={detail.identity.risk_level} />
              <KV label="State" value={detail.identity.state} /><KV label="Verification" value={detail.identity.verification_status} />
              <KV label="Last Check" value={fmtDate(detail.identity.last_check)} />
            </div>
          ) },
          { key: "incidents", label: `Incidents (${detail.incidents.length})`, content: (
            <Table testid="asset360-incidents" empty="No incidents" rows={detail.incidents} columns={[
              { key: "incident_id", label: "ID", render: (r) => <span className="font-mono text-xs">{r.incident_id}</span> }, { key: "title", label: "Title" },
              { key: "risk_level", label: "Risk", render: (r) => <RiskBadge value={r.risk_level} /> }, { key: "state", label: "State", render: (r) => <StatusBadge value={r.state} /> },
            ]} />
          ) },
          { key: "threats", label: `Threats (${detail.threats.length})`, content: (
            <Table testid="asset360-threats" empty="No threats" rows={detail.threats} columns={[
              { key: "threat_id", label: "ID", render: (r) => <span className="font-mono text-xs">{r.threat_id}</span> }, { key: "category", label: "Category" },
              { key: "risk_level", label: "Risk", render: (r) => <RiskBadge value={r.risk_level} /> }, { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
            ]} />
          ) },
          { key: "simulations", label: `Simulations (${detail.simulations.length})`, content: (
            <Table testid="asset360-sims" empty="No simulations" rows={detail.simulations} columns={[
              { key: "id", label: "ID", render: (r) => <span className="font-mono text-xs">{r.simulation_id || r.id}</span> }, { key: "status", label: "Status" },
            ]} />
          ) },
          { key: "timeline", label: "Timeline", content: <Timeline events={detail.timeline} /> },
        ] : []} />
    </div>
  );
}
